
import { computed, defineComponent, onMounted, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

// module components
import CarrierForm from '@/modules/CarriersModule/components/CarrierForm/index.vue'
import CarrierSendModal from '@/modules/CarriersModule/components/CarrierSendModal/index.vue'
import CarriersTableActions from '@/modules/CarriersModule/components/CarriersTableActions/index.vue'
import CarrierTripartiteAgreemeentModal from '@/modules/CarriersModule/components/CarrierTripartiteAgreementModal/index.vue'

// realisations
import { carriers } from '@/core/realisations/carriers.realisation'

// types
import {
    CarrierT,
    ActivityListT,
    CarrierCreatePayloadT,
    CarrierUpdatePayloadT,
} from '@/core/types/carriers.types'
import { CourierT } from '@/core/types/couriers.types'

// classes
import { DataState } from '@/core/classes/DataState.class'
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

// constants
import { CarriersColumns } from '@/core/constants/columns/Carriers.columns'

// enums
import { CarriersStatusesEnum } from '@/core/enums/carriers.enums'
import { companies } from '@/core/realisations/companies.realisation'
import useAccount from '@/common/composable/useAccount'
import { useFile } from '@/common/composable/useFile'

import { formatDateFromBackend } from '@/common/helpers/formatDate'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        'failed-fetch': FailedFetch,
        'carrier-form': CarrierForm,
        'carrier-send': CarrierSendModal,
        checkbox: Checkbox,
        'table-scroll-wrapper': TableScrollWrapper,
        'the-table': TheTable,
        'carriers-table-actions': CarriersTableActions,
        Cheap,
        'carrier-tripartite-agreemeent-modal': CarrierTripartiteAgreemeentModal,
    },
    setup() {
        const { havePermissions } = useAccount()
        const { saveFile } = useFile()

        const state = reactive({
            search: undefined as string | undefined,
            bin: undefined as string | undefined,
            isStandardContract: 0 as number,
            isTripartiteContract: 0 as number,
            isSupplier: false,
            isCourierSupplier: false,
            isCarrier: true,
            typeTons: 0,
            //
            limit: 50,
        })

        const state_send = reactive({
            is_opened: false,
            //
            id: null as number | null,
            bin: null as string | null,
            couriers: [] as CourierT[],
        })

        const state_jira = reactive({
            is_loading: false,
        })

        const state_deactivate = reactive({
            is_loading: false,
        })

        const excel_state = reactive({
            is_loading: false,
        })

        const carrier_tripartite_agreement_state = reactive({
            is_opened: false,
            initial_value: null as CarrierT | null,
        })

        const onCarrierTripartiteAgreement = (carrier: CarrierT) => {
            carrier_tripartite_agreement_state.initial_value = { ...carrier }
            carrier_tripartite_agreement_state.is_opened = true
        }

        const onCarrierTripartiteAgreementCancel = () => {
            carrier_tripartite_agreement_state.is_opened = false
            carrier_tripartite_agreement_state.initial_value = null
        }

        const data_state = reactive(new DataState<CarrierT>({}))

        const notifications = useNotifications()

        const loadCarriers = async (page: number) => {
            data_state.pagination.current_page = page

            let activity = state.isSupplier
                ? 'Supplier'
                : state.isCourierSupplier
                ? 'Courier supplier'
                : state.isCarrier
                ? 'Carrier'
                : null

            try {
                data_state.is_loading = true
                const data = await carriers.getList({
                    page,
                    search: state.search,
                    bin: state.bin,
                    limit: state.limit,
                    isStandardContract: state.isStandardContract,
                    isTripartiteContract: state.isTripartiteContract,
                    activity,
                    typeTons: state.typeTons,
                })

                data_state.list = data.data
                data_state.pagination.total_pages = data.meta.last_page
                data_state.pagination.total_items = data.meta.total

                data_state.is_failed = false
                data_state.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.failure.catchError(error)

                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_failed = true
                }
            } finally {
                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_loading = false
                }
            }
        }

        const exportExcel = async () => {
            try {
                excel_state.is_loading = true

                const params = {
                    search: state.search,
                    bin: state.bin,
                    activity: getActivity.value,
                }

                const response = await carriers.exportExcel(params)
                saveFile(response, 'Поставщики')
            } catch (e) {
                console.log(e)
            } finally {
                excel_state.is_loading = false
            }
        }

        const reset = () => {
            state.bin = undefined
            state.search = undefined
            state.isStandardContract = 0
            state.isTripartiteContract = 0
            state.isSupplier = false
            state.isCourierSupplier = false

            loadCarriers(1)
        }

        const onLimitChange = (limit: number) => {
            state.limit = limit
            loadCarriers(data_state.pagination.current_page)
        }

        //

        const getActivity = computed(() =>
            state.isSupplier
                ? 'Supplier'
                : state.isCourierSupplier
                ? 'Courier supplier'
                : state.isCarrier
                ? 'Carrier'
                : undefined
        )

        //

        onMounted(() => loadCarriers(1))

        //

        // Add or update carrier
        const { actions_state, startCreate, startUpdate, closeActions } = useActions()

        const createCarrier = async (carrier_payload: CarrierCreatePayloadT) => {
            try {
                actions_state.is_actions_loading = true

                const response = await carriers.createCarrier(carrier_payload)
                data_state.list.push(response.data)

                actions_state.is_actions_opened = false
                notifications.notify({
                    title: response.message || 'Перевоз',
                    type: 'success',
                })

                await loadCarriers(1)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании перевозчика произошла ошибка!',
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateCarrier = async ({
            carrier_id,
            carrier_payload,
        }: {
            carrier_id: number
            carrier_payload: CarrierUpdatePayloadT
        }) => {
            try {
                actions_state.is_actions_loading = true

                await carriers.updateCarrier(carrier_id, carrier_payload)
                if (carrier_payload.vehicleOwner) {
                    await carriers.updateVehicleOwner(carrier_payload.vehicleOwner)
                }

                closeActions()

                notifications.notify({
                    title: 'Перевозчик сохранен',
                    type: 'success',
                })

                await loadCarriers(data_state.pagination.current_page)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При редактировании перевозчика произошла ошибка!',
                })
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const cutName = (name: string, max_length: number): string => {
            if (name && name.length > max_length) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, max_length - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        const getActivityString = (activity: ActivityListT[]) => {
            if (!activity) return ''

            let result: string[] = []

            activity.forEach((a) => {
                if (a.activityId === 2) result.push('Перевозчик')
                else if (a.activityId === 3) result.push('Поставщик')
                else if (a.activityId === 4) result.push('Поставщик курьеров')
            })

            return result ? result.join(', ') : ''
        }

        const onIsSupplier = (value: boolean) => {
            if (value) {
                state.isCourierSupplier = false
                state.isCarrier = false
            }
        }

        const onIsCourierSupplier = (value: boolean) => {
            if (value) {
                state.isSupplier = false
                state.isCarrier = false
            }
        }

        const onIsCarrier = (value: boolean) => {
            if (value) {
                state.isSupplier = false
                state.isCourierSupplier = false
            }
        }

        const onSend = (carrier: CarrierT) => {
            state_send.is_opened = true

            state_send.id = carrier.id
            state_send.bin = carrier.bin
            state_send.couriers = carrier.couriers
        }

        const onCancelSend = () => {
            state_send.is_opened = false

            state_send.id = null
            state_send.bin = null
            state_send.couriers = []
        }

        const addCarriersJira = async () => {
            try {
                state_jira.is_loading = true
                await carriers.addCarriersJira()
                notifications.notify({ title: 'Перевозчики созданы!', type: 'success' })
            } catch (error: any) {
                if (!error.length) {
                    notifications.notify({
                        title: 'При создании курьеров произошла ошбика!',
                        type: 'danger',
                    })
                } else {
                    notifications.notifyError({
                        error,
                        title: `Ошибка при создании перевозчиков!`,
                    })
                }
            } finally {
                state_jira.is_loading = false
            }
        }

        const activateCarrier = async (carrier_id: number) => {
            const answer = confirm('Вы действительно хотите активировать поставщика?')
            if (!answer) return

            try {
                state_deactivate.is_loading = true

                await companies.confirmCarrier(carrier_id)
                notifications.notify({ title: 'Письмо отправлено на почту!', type: 'success' })

                loadCarriers(1)
            } catch (error) {
                notifications.notifyError({
                    title: 'При активации поставщика произошла ошибка',
                    error,
                })
            } finally {
                state_deactivate.is_loading = false
            }
        }

        const deactivateCarrier = async (carrier_id: number) => {
            const answer = confirm('Вы действительно хотите деактивировать поставщика?')
            if (!answer) return

            try {
                state_deactivate.is_loading = true

                await companies.deactivateCompany(carrier_id)
                notifications.notify({ title: 'Поставщик деактивирован', type: 'success' })

                loadCarriers(1)
            } catch (error) {
                notifications.notifyError({
                    title: 'При деактивации поставщика произошла ошибка',
                    error,
                })
            } finally {
                state_deactivate.is_loading = false
            }
        }

        return {
            havePermissions,
            //
            state,
            state_send,
            state_jira,
            excel_state,
            //
            cutName,
            data_state,
            loadCarriers,
            actions_state,
            startCreate,
            startUpdate,
            createCarrier,
            updateCarrier,
            reset,
            getActivityString,
            onIsSupplier,
            onIsCourierSupplier,
            onIsCarrier,
            onSend,
            onCancelSend,
            addCarriersJira,
            closeActions,
            activateCarrier,
            deactivateCarrier,
            onLimitChange,
            exportExcel,
            //
            CarriersColumns,
            CarriersStatusesEnum,
            //
            carrier_tripartite_agreement_state,
            onCarrierTripartiteAgreement,
            onCarrierTripartiteAgreementCancel,
            formatDateFromBackend,
        }
    },
})
